/* eslint-disable camelcase */
/* eslint-disable consistent-return */
/**
 * @file 用户相关私有配置，包括：用户信息、登录状态、资产列表、用户配置法币币种
 * @date 2021-07-03
 */

import { userapi } from '@/apis/api2';
import { getUserInfo } from '@/utils/userInfo';
import { remove as removeToken } from '@/utils/token';
import { saLogin } from '@/utils/sensors';
import { error as errorToast } from '@/utils/toast';
import { reportToken } from '@/utils/monitor';

const state = {
    /**
     * UC 用户数据，使用前请先根据 `userstate` 状态进行判断
     */
    info: {},

    /**
     * 用户状态
     * - `0` 初始化
     * - `-1` 未登录
     * - `1` 已登录
     */
    userstate: 0,

    /**
     * kyc用户信息
     */
    kycInfo: {},

    /**
     * 用户类型
     * - '0' 普通用户
     * - '1' 代理商
     */
    type: '',

    /**
     * 用户邀请码
     */
    inviteCode: '',

    /**
     * UTA 用户当前保证金模式
     */
    marginModeValue: '',
};

const getters = {
    isLogin: (state) => state.userstate === 1, // 是否已经登录
    // kyc等级
    kycLevel: (state) => {
        let level = 0;

        // 个人kyc接口拿到的是 LEVEL_DEFAULT、LEVEL_1、LEVEL_2 的字段
        if (/LEVEL_(\d)/.test(String(state.kycInfo.applicant?.level))) {
            level = Number(RegExp.$1);
            // 企业kyc判断
        } else if (state.info.member_tag?.KYC === '1') {
            level = 10;
        }

        return level;
    },
    isKyc: (state, getters) => getters.kycLevel > 0, // 是否通过kyc
    // 邀请链接，需要组合邀请码
    inviteLink: (state) => `${window.location.origin}/invite?ref=${encodeURIComponent(state.inviteCode)}`,
    // 用户账户是否是统一账户
    // eslint-disable-next-line camelcase
    isUTA: (state) => state.info?.member_tag?.UTA === 'SUCCESS',
    // 用户是否为资金用户
    isFA: (state) => state.info?.grayFlag?.IsFA,
    // 用户是否设置下单时弹出二次确认弹框
    isOrderConfirm: (state) => state.info?.double_confirm?.includes('orderConfirm'),
    // 用户是否设置取消全部订单时弹出二次确认弹框
    isCancelAllConfirm: (state) => state.info?.double_confirm?.includes('cancelAllConfirm'),
    uid: (state) => state.info?.id,
    // 是否是子账号
    isSub: (state) => state.info.is_submember,
};

const mutations = {
    SET_USERSTATE(state, payload) {
        state.userstate = payload;
    },
    SET_INFO(state, payload) {
        state.info = payload;
    },
    // 设置kyc用户信息
    SET_KYC_INFO(state, payload) {
        state.kycInfo = payload;
    },
    // 设置用户类型
    SET_TYPE(state, payload) {
        state.type = payload;
    },
    // 设置用户邀请码
    SET_INVITE_CODE(state, payload) {
        state.inviteCode = payload;
    },
    // 设置用户保证金模式
    SET_MARGIN_MODE(state, payload) {
        state.marginModeValue = payload;
    },
};

const actions = {
    // 退出登录
    // ✅ 0. 删除 Token
    // ✅ 1. 重置登录状态为 -1
    // ✅ 2. 清空用户信息数据
    // ✅ 3. 清空用户资产数据
    // ✅ 4. 重置法币币种设置
    // 🚫 5. 清空自选交易对数据
    async LOGOUT({ commit, dispatch }, { useapi = true } = {}) {
        // 目前业务场景，只有在手动触发logout时，才会调用退出接口，并清除token；
        // 接口登录态失效，清除token的操作在utils/service中统一处理；
        // 接口服务调用失败，只重置状态，不清除token；
        if (useapi) {
            await userapi.logout();
            removeToken();
        }
        commit('SET_USERSTATE', -1);
        commit('SET_INFO', {});
        commit('SET_KYC_INFO', {});
        commit('SET_TYPE', '');
        commit('SET_INVITE_CODE', '');
        dispatch('settings/INIT_LEGAL', null, { root: true });
        await dispatch('balance/LOGOUT', null, { root: true });
    },

    // 初始化用户信息
    async INIT({ commit, dispatch }) {
        const userInfo = await getUserInfo();
        const pageStatus = window.navigator.onLine;

        reportToken('init');
        window.sensors?.quick('autoTrack');

        if (!userInfo) {
            // 页面离线状态
            if (!pageStatus) {
                errorToast('Network Error!');
                return;
            }

            if (state.userstate !== -1) {
                dispatch('LOGOUT', { useapi: false });
            }

            reportToken('profile_error');
            return;
        }

        commit('SET_INFO', userInfo);
        commit('SET_USERSTATE', 1);
        dispatch('settings/INIT_LEGAL', null, { root: true });
        window.sensors?.registerPage({
            // eslint-disable-next-line camelcase
            is_ominibus_acc: state.info?.member_tag?.UTA === 'SUCCESS',
        });
        window.GA_UID = userInfo.id;
        saLogin(userInfo.id);
    },
    // 获取设置kyc用户信息
    async GET_KYC_INFO({ commit, getters }) {
        if (!getters.isLogin) return;

        const res = await userapi.getKyc({
            obtain_current: true,
            obtain_kyc_token: false,
            obtain_quotas: false,
            obtain_state: false,
        });

        if (res.success) {
            commit('SET_KYC_INFO', res.result);
        }
    },
    // 获取用户邀请码
    // 未登录和已获取过无需再次获取
    async GET_INVITE_CODE({ state, commit, getters }) {
        if (!getters.isLogin || state.inviteCode) return;
        const { success, result } = await userapi.getInvitationCode({ client_id: state.info.id, scene: 2 });
        if (!success) return;
        if (result.affiliate_id) {
            commit('SET_TYPE', result.affiliate_id);
        }
        // 注意：只有状态为0的邀请码才可用
        if (result.data.length && Number(result.data[0].status) === 0 && result.data[0].code) {
            commit('SET_INVITE_CODE', result.data[0].code);
        }
    },
    // 设置用户当前保证金模式
    async SET_CURRENT_MARGIN_MODE({
        state,
        commit,
        dispatch,
        rootState,
    }, marginMode) {
        if (marginMode !== state.marginModeValue) {
            commit('SET_MARGIN_MODE', marginMode);
            if (rootState.leverage.isTurnOn !== null) {
                dispatch('leverage/GET_LEVERAGE_INFO_WITH_UTA', null, { root: true });
            }
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
